<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import * as echarts from 'echarts'

const props = defineProps({
  inbound: {
    type: Object,
    default: () => ({ portTotals: [], ipTotals: [], links: [] })
  },
  outbound: {
    type: Object,
    default: () => ({ portTotals: [], ipTotals: [], links: [] })
  },
  centeredIp: {
    type: String,
    required: true
  },
  height: {
    type: String,
    default: '400px'
  },
  maxPorts: {
    type: Number,
    default: 10
  },
  maxIps: {
    type: Number,
    default: 20
  }
})

const emit = defineEmits(['node-click', 'edge-click'])

// Color scheme
const COLORS = {
  sourceIp: '#4A90D9',
  sourcePort: '#6B7280',
  centeredIp: '#9B7EE8',
  destPort: '#6B7280',
  destIp: '#50C878',
  other: '#9CA3AF',
  centerGlow: 'rgba(115, 87, 210, 0.4)'
}

const chartContainer = ref(null)
let chartInstance = null
let resizeObserver = null

// Process data into Sankey nodes and links
const sankeyData = computed(() => {
  const hasInbound = props.inbound.portTotals?.length > 0
  const hasOutbound = props.outbound.portTotals?.length > 0

  if (!props.centeredIp || (!hasInbound && !hasOutbound)) {
    return { nodes: [], links: [] }
  }

  const nodes = []
  const links = []

  // === PROCESS INBOUND ===
  if (hasInbound) {
    // Get accurate totals from terms aggregations
    const portTotals = new Map(props.inbound.portTotals.map(p => [String(p.value), p.count]))
    const ipTotals = new Map(props.inbound.ipTotals.map(ip => [String(ip.value), ip.count]))

    // Build link map from composite data (IP|Port -> count)
    const linkMap = new Map()
    props.inbound.links.forEach(link => {
      const ip = link.values['id.orig_h']
      const port = link.values['id.resp_p']
      if (ip && port) {
        linkMap.set(`${ip}|${port}`, link.count)
      }
    })

    // Calculate totals
    const totalIncoming = Array.from(portTotals.values()).reduce((sum, c) => sum + c, 0)

    // Select top ports and IPs
    const topPorts = props.inbound.portTotals.slice(0, props.maxPorts)
    const topIps = props.inbound.ipTotals.slice(0, props.maxIps)
    const topPortSet = new Set(topPorts.map(p => String(p.value)))
    const topIpSet = new Set(topIps.map(ip => String(ip.value)))

    // Calculate "Other" totals
    const topPortsTotal = topPorts.reduce((sum, p) => sum + p.count, 0)
    const topIpsTotal = topIps.reduce((sum, ip) => sum + ip.count, 0)
    const otherPortsTotal = totalIncoming - topPortsTotal
    const otherIpsTotal = totalIncoming - topIpsTotal

    // Track how much of each port's traffic comes from displayed IPs
    const portFromDisplayedIps = new Map()

    // Create IP nodes (level 0)
    topIps.forEach(ip => {
      const ipStr = String(ip.value)
      nodes.push({
        name: `src_${ipStr}`,
        label: ipStr,
        itemStyle: { color: COLORS.sourceIp },
        depth: 0,
        type: 'ip',
        value: ipStr,
        direction: 'source',
        totalCount: ip.count
      })
    })

    // "Other IPs" node if needed
    if (otherIpsTotal > 0) {
      const otherIpCount = props.inbound.ipTotals.length - topIps.length
      nodes.push({
        name: 'other_src_ips',
        label: otherIpCount > 0 ? `Other (${otherIpCount}+)` : 'Other',
        itemStyle: { color: COLORS.other },
        depth: 0,
        type: 'other',
        value: null,
        direction: 'source',
        totalCount: otherIpsTotal,
        excludeIps: Array.from(topIpSet)
      })
    }

    // Create Port nodes (level 1)
    topPorts.forEach(port => {
      const portStr = String(port.value)
      nodes.push({
        name: `srcport_${portStr}`,
        label: portStr,
        itemStyle: { color: COLORS.sourcePort },
        depth: 1,
        type: 'port',
        value: portStr,
        direction: 'source',
        totalCount: port.count
      })
    })

    // "Other Ports" node if needed
    if (otherPortsTotal > 0) {
      const otherPortCount = props.inbound.portTotals.length - topPorts.length
      nodes.push({
        name: 'other_src_ports',
        label: otherPortCount > 0 ? `Other (${otherPortCount}+)` : 'Other',
        itemStyle: { color: COLORS.other },
        depth: 1,
        type: 'other',
        value: null,
        direction: 'source',
        totalCount: otherPortsTotal,
        excludePorts: Array.from(topPortSet)
      })
    }

    // Create links: IP -> Port. Only real (IP, port) pairs from the aggregation
    // become IP→port edges; each IP's remaining volume (its connections to ports
    // outside its real top-pair set) is routed to the aggregate "Other Ports"
    // node. We deliberately do NOT smear the remainder across displayed ports —
    // that fabricates specific IP→port edges that don't exist and return no
    // results when clicked.
    topIps.forEach(ip => {
      const ipStr = String(ip.value)
      let ipToDisplayed = 0
      const ipPortLinks = new Map() // Real links from aggregation data

      // Collect exact links from the aggregation (nested_terms) pairs
      topPorts.forEach(port => {
        const portStr = String(port.value)
        const linkCount = linkMap.get(`${ipStr}|${portStr}`) || 0
        if (linkCount > 0) {
          ipPortLinks.set(portStr, linkCount)
          ipToDisplayed += linkCount
        }
      })

      // Remaining volume goes to "Other Ports" (the long tail of ports this IP
      // hit that aren't displayed), never to specific displayed ports.
      const unaccountedTraffic = ip.count - ipToDisplayed
      if (unaccountedTraffic > 0 && otherPortsTotal > 0) {
        links.push({
          source: `src_${ipStr}`,
          target: 'other_src_ports',
          value: unaccountedTraffic
        })
      }

      // Create the actual links from the real aggregation data
      ipPortLinks.forEach((count, portStr) => {
        if (count > 0) {
          links.push({
            source: `src_${ipStr}`,
            target: `srcport_${portStr}`,
            value: count
          })
          portFromDisplayedIps.set(portStr, (portFromDisplayedIps.get(portStr) || 0) + count)
        }
      })
    })

    // "Other IPs" -> Port links
    if (otherIpsTotal > 0) {
      let otherIpsToOtherPorts = otherIpsTotal

      topPorts.forEach(port => {
        const portStr = String(port.value)
        const portTotal = port.count
        const fromDisplayed = portFromDisplayedIps.get(portStr) || 0
        const fromOther = portTotal - fromDisplayed
        if (fromOther > 0) {
          links.push({
            source: 'other_src_ips',
            target: `srcport_${portStr}`,
            value: fromOther
          })
          otherIpsToOtherPorts -= fromOther
        }
      })

      // "Other IPs" -> "Other Ports" link
      if (otherIpsToOtherPorts > 0 && otherPortsTotal > 0) {
        links.push({
          source: 'other_src_ips',
          target: 'other_src_ports',
          value: otherIpsToOtherPorts
        })
      }
    }

    // Port -> Center links
    topPorts.forEach(port => {
      links.push({
        source: `srcport_${String(port.value)}`,
        target: `center_${props.centeredIp}`,
        value: port.count
      })
    })

    if (otherPortsTotal > 0) {
      links.push({
        source: 'other_src_ports',
        target: `center_${props.centeredIp}`,
        value: otherPortsTotal
      })
    }
  }

  // === CENTER NODE ===
  const totalIncoming = hasInbound
    ? props.inbound.portTotals.reduce((sum, p) => sum + p.count, 0)
    : 0
  const totalOutgoing = hasOutbound
    ? props.outbound.portTotals.reduce((sum, p) => sum + p.count, 0)
    : 0

  nodes.push({
    name: `center_${props.centeredIp}`,
    label: props.centeredIp,
    itemStyle: { color: COLORS.centeredIp },
    depth: 2,
    type: 'ip',
    value: props.centeredIp,
    direction: 'center',
    totalIncoming,
    totalOutgoing
  })

  // === PROCESS OUTBOUND ===
  if (hasOutbound) {
    const portTotals = new Map(props.outbound.portTotals.map(p => [String(p.value), p.count]))
    const ipTotals = new Map(props.outbound.ipTotals.map(ip => [String(ip.value), ip.count]))

    const linkMap = new Map()
    props.outbound.links.forEach(link => {
      const port = link.values['id.resp_p']
      const ip = link.values['id.resp_h']
      if (port && ip) {
        linkMap.set(`${port}|${ip}`, link.count)
      }
    })

    const topPorts = props.outbound.portTotals.slice(0, props.maxPorts)
    const topIps = props.outbound.ipTotals.slice(0, props.maxIps)
    const topPortSet = new Set(topPorts.map(p => String(p.value)))
    const topIpSet = new Set(topIps.map(ip => String(ip.value)))

    const topPortsTotal = topPorts.reduce((sum, p) => sum + p.count, 0)
    const topIpsTotal = topIps.reduce((sum, ip) => sum + ip.count, 0)
    const otherPortsTotal = totalOutgoing - topPortsTotal
    const otherIpsTotal = totalOutgoing - topIpsTotal

    const ipFromDisplayedPorts = new Map()

    // Create Port nodes (level 3)
    topPorts.forEach(port => {
      const portStr = String(port.value)
      nodes.push({
        name: `dstport_${portStr}`,
        label: portStr,
        itemStyle: { color: COLORS.destPort },
        depth: 3,
        type: 'port',
        value: portStr,
        direction: 'dest',
        totalCount: port.count
      })
    })

    if (otherPortsTotal > 0) {
      const otherPortCount = props.outbound.portTotals.length - topPorts.length
      nodes.push({
        name: 'other_dst_ports',
        label: otherPortCount > 0 ? `Other (${otherPortCount}+)` : 'Other',
        itemStyle: { color: COLORS.other },
        depth: 3,
        type: 'other',
        value: null,
        direction: 'dest',
        totalCount: otherPortsTotal,
        excludePorts: Array.from(topPortSet)
      })
    }

    // Create IP nodes (level 4)
    topIps.forEach(ip => {
      const ipStr = String(ip.value)
      nodes.push({
        name: `dst_${ipStr}`,
        label: ipStr,
        itemStyle: { color: COLORS.destIp },
        depth: 4,
        type: 'ip',
        value: ipStr,
        direction: 'dest',
        totalCount: ip.count
      })
    })

    if (otherIpsTotal > 0) {
      const otherIpCount = props.outbound.ipTotals.length - topIps.length
      nodes.push({
        name: 'other_dst_ips',
        label: otherIpCount > 0 ? `Other (${otherIpCount}+)` : 'Other',
        itemStyle: { color: COLORS.other },
        depth: 4,
        type: 'other',
        value: null,
        direction: 'dest',
        totalCount: otherIpsTotal,
        excludeIps: Array.from(topIpSet)
      })
    }

    // Center -> Port links
    topPorts.forEach(port => {
      links.push({
        source: `center_${props.centeredIp}`,
        target: `dstport_${String(port.value)}`,
        value: port.count
      })
    })

    if (otherPortsTotal > 0) {
      links.push({
        source: `center_${props.centeredIp}`,
        target: 'other_dst_ports',
        value: otherPortsTotal
      })
    }

    // Port -> IP links. Only real (port, IP) pairs from the aggregation become
    // port→IP edges; each port's remaining volume (its connections to IPs
    // outside its real top-pair set) is routed to the aggregate "Other IPs"
    // node. We deliberately do NOT smear the remainder across displayed IPs —
    // that fabricates specific port→IP edges that don't exist and return no
    // results when clicked.
    topPorts.forEach(port => {
      const portStr = String(port.value)
      let portToDisplayed = 0
      const portIpLinks = new Map() // Real links from aggregation data

      // Collect exact links from the aggregation (nested_terms) pairs
      topIps.forEach(ip => {
        const ipStr = String(ip.value)
        const linkCount = linkMap.get(`${portStr}|${ipStr}`) || 0
        if (linkCount > 0) {
          portIpLinks.set(ipStr, linkCount)
          portToDisplayed += linkCount
        }
      })

      // Remaining volume goes to "Other IPs" (the long tail of destinations this
      // port reached that aren't displayed), never to specific displayed IPs.
      const unaccountedTraffic = port.count - portToDisplayed
      if (unaccountedTraffic > 0 && otherIpsTotal > 0) {
        links.push({
          source: `dstport_${portStr}`,
          target: 'other_dst_ips',
          value: unaccountedTraffic
        })
      }

      // Create the actual links from the real aggregation data
      portIpLinks.forEach((count, ipStr) => {
        if (count > 0) {
          links.push({
            source: `dstport_${portStr}`,
            target: `dst_${ipStr}`,
            value: count
          })
          ipFromDisplayedPorts.set(ipStr, (ipFromDisplayedPorts.get(ipStr) || 0) + count)
        }
      })
    })

    // "Other Ports" -> IP links
    if (otherPortsTotal > 0) {
      let otherPortsToOtherIps = otherPortsTotal

      topIps.forEach(ip => {
        const ipStr = String(ip.value)
        const ipTotal = ip.count
        const fromDisplayed = ipFromDisplayedPorts.get(ipStr) || 0
        const fromOther = ipTotal - fromDisplayed
        if (fromOther > 0) {
          links.push({
            source: 'other_dst_ports',
            target: `dst_${ipStr}`,
            value: fromOther
          })
          otherPortsToOtherIps -= fromOther
        }
      })

      // "Other Ports" -> "Other IPs" link
      if (otherPortsToOtherIps > 0 && otherIpsTotal > 0) {
        links.push({
          source: 'other_dst_ports',
          target: 'other_dst_ips',
          value: otherPortsToOtherIps
        })
      }
    }
  }

  return { nodes, links }
})

// Watch for data changes
watch([() => props.inbound, () => props.outbound, () => props.centeredIp, chartContainer], () => {
  if (chartContainer.value) {
    setTimeout(() => updateChart(), 100)
  }
}, { immediate: false, deep: true })

function updateChart() {
  if (!chartContainer.value) return

  if (!chartInstance) {
    chartInstance = echarts.init(chartContainer.value)
  }

  const { nodes, links } = sankeyData.value

  if (nodes.length === 0 || links.length === 0) {
    chartInstance.setOption({
      title: {
        text: 'No network flow data available',
        left: 'center',
        top: 'center',
        textStyle: { color: '#6D7785', fontSize: 14 }
      },
      series: []
    }, true)
    return
  }

  // Build lookup maps
  const nodeLabels = {}
  const nodeMeta = {}
  nodes.forEach(n => {
    nodeLabels[n.name] = n.label
    nodeMeta[n.name] = {
      type: n.type,
      direction: n.direction,
      totalCount: n.totalCount,
      totalIncoming: n.totalIncoming,
      totalOutgoing: n.totalOutgoing,
      excludeIps: n.excludeIps || null,
      excludePorts: n.excludePorts || null
    }
  })

  // Determine margins
  const hasInbound = nodes.some(n => n.direction === 'source')
  const hasOutbound = nodes.some(n => n.direction === 'dest')
  let leftMargin = 120
  let rightMargin = 120
  if (hasInbound && !hasOutbound) {
    rightMargin = '50%'
  }

  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      backgroundColor: '#1E252F',
      borderColor: '#2a3441',
      textStyle: { color: '#fff', fontSize: 12 },
      formatter: (params) => {
        if (params.dataType === 'node') {
          const label = nodeLabels[params.data.name] || params.data.name
          const meta = nodeMeta[params.data.name]
          if (meta) {
            if (meta.direction === 'center') {
              const lines = [`<strong>${label}</strong>`]
              if (meta.totalIncoming > 0) {
                lines.push(`Incoming: ${meta.totalIncoming.toLocaleString()} connections`)
              }
              if (meta.totalOutgoing > 0) {
                lines.push(`Outgoing: ${meta.totalOutgoing.toLocaleString()} connections`)
              }
              return lines.join('<br/>')
            }
            if (meta.totalCount) {
              const directionLabel = meta.direction === 'source' ? 'Incoming' : 'Outgoing'
              return `<strong>${label}</strong><br/>${directionLabel}: ${meta.totalCount.toLocaleString()} connections`
            }
          }
          return `<strong>${label}</strong>`
        } else if (params.dataType === 'edge') {
          const sourceLabel = nodeLabels[params.data.source] || params.data.source
          const targetLabel = nodeLabels[params.data.target] || params.data.target
          const value = typeof params.data.value === 'number' ? params.data.value.toLocaleString() : params.data.value
          return `${sourceLabel} → ${targetLabel}<br/>Connections: <strong>${value}</strong>`
        }
        return ''
      }
    },
    series: [{
      type: 'sankey',
      layout: 'none',
      left: leftMargin,
      right: rightMargin,
      top: 10,
      bottom: 10,
      emphasis: { focus: 'adjacency' },
      nodeAlign: 'justify',
      nodeGap: 12,
      nodeWidth: 20,
      layoutIterations: 0,
      draggable: false,
      data: nodes.map(node => {
        const isCenter = node.direction === 'center'
        const isOther = node.type === 'other'
        return {
          name: node.name,
          depth: node.depth,
          itemStyle: {
            ...node.itemStyle,
            ...(isCenter ? { shadowColor: COLORS.centerGlow, shadowBlur: 20 } : {})
          },
          draggable: false,
          label: {
            show: true,
            position: node.depth < 2 ? 'left' : (node.depth > 2 ? 'right' : 'inside'),
            color: isOther ? '#9CA3AF' : '#FFFFFF',
            fontSize: isOther ? 10 : (isCenter ? 16 : 11),
            fontWeight: isCenter ? 'bold' : 'normal',
            fontStyle: isOther ? 'italic' : 'normal',
            formatter: () => node.label
          },
          _meta: { type: node.type, value: node.value, direction: node.direction }
        }
      }),
      links: links,
      lineStyle: { color: 'gradient', curveness: 0.5, opacity: 0.4 },
      label: { color: '#E5E7EB', fontSize: 11 }
    }]
  }

  chartInstance.setOption(option, true)

  chartInstance.off('click')
  chartInstance.on('click', (params) => {
    if (params.dataType === 'node') {
      const nodeData = nodes.find(n => n.name === params.data.name)
      if (nodeData && nodeData.direction !== 'center') {
        emit('node-click', {
          type: nodeData.type,
          value: nodeData.value,
          direction: nodeData.direction,
          excludeIps: nodeData.excludeIps || null,
          excludePorts: nodeData.excludePorts || null
        })
      }
    } else if (params.dataType === 'edge') {
      const sourceNode = nodes.find(n => n.name === params.data.source)
      const targetNode = nodes.find(n => n.name === params.data.target)
      emit('edge-click', {
        source: sourceNode ? {
          type: sourceNode.type,
          value: sourceNode.value,
          direction: sourceNode.direction,
          excludeIps: sourceNode.excludeIps || null,
          excludePorts: sourceNode.excludePorts || null
        } : null,
        target: targetNode ? {
          type: targetNode.type,
          value: targetNode.value,
          direction: targetNode.direction,
          excludeIps: targetNode.excludeIps || null,
          excludePorts: targetNode.excludePorts || null
        } : null,
        value: params.data.value
      })
    }
  })
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  // Reflow on container size changes too — the parent grows this chart's height
  // dynamically with the node count, and the tool-rail can change its width;
  // neither fires a window resize.
  if (chartContainer.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => handleResize())
    resizeObserver.observe(chartContainer.value)
  }
  const hasData = props.inbound.portTotals?.length > 0 || props.outbound.portTotals?.length > 0
  if (chartContainer.value && hasData) {
    setTimeout(() => updateChart(), 100)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (chartInstance) {
    chartInstance.dispose()
  }
})

function handleResize() {
  if (chartInstance) {
    try {
      chartInstance.resize()
    } catch (e) {
      console.error('Error resizing sankey chart:', e)
    }
  }
}

defineExpose({ resize: handleResize })
</script>

<template>
  <div class="sankey-wrapper" :style="{ height: height }">
    <div class="direction-indicators">
      <div class="direction-label incoming">
        <span>Incoming</span>
        <span class="direction-icon">→</span>
      </div>
      <div class="direction-label center-label">Focus IP</div>
      <div class="direction-label outgoing">
        <span class="direction-icon">→</span>
        <span>Outgoing</span>
      </div>
    </div>
    <div ref="chartContainer" class="sankey-chart"></div>
  </div>
</template>

<style scoped>
.sankey-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.direction-indicators {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6rem;
  margin-bottom: 0.5rem;
}

.direction-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.direction-label.incoming {
  color: #4A90D9;
}

.direction-label.outgoing {
  color: #50C878;
}

.direction-label.center-label {
  color: #7357D2;
  font-weight: 600;
}

.direction-icon {
  font-size: 0.875rem;
}

.sankey-chart {
  width: 100%;
  flex: 1;
  min-height: 0;
  cursor: pointer;
}
</style>
